import {
  ADMIN_RECEIVABLES_LOCATION,
  AdminDollarInput2,
  AdminHeader,
  AdminInput,
  AdminSpinner,
  AdminSwitch,
  AnimatedDiv,
  Button,
  ChevronSmall,
  CollapsibleComponent,
  Disclaimer,
  Div,
  ErrorToast,
  H3,
  H5,
  Icon,
  Img,
  ManageLocationVDriveRequestData,
  P,
  Radio,
  SubTitleWithTooltipIcon,
  SuccessToast,
  VDriveLogo,
  VDrivePaymentMode,
  VDriveStatus,
  api,
  getLocationFleetOrganization,
  getLocationVDriveById,
  groupFundingTipSchedule,
  lightTheme,
  sanitizeDeliveryAssignment,
  unGroupFundingTipSchedule,
  useMeasuredHeight,
  useVDrive,
  vDriveDeactivated,
  reportError,
} from '@vgrubs/components';
import { isAxiosError } from 'axios';
import {
  DeliveryAssignmentEntity,
  ScheduleEntity,
  VDriveProviderInfo,
} from 'components/src/types/location.types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router';
import styled from 'styled-components';
import { AddEditFundingTip } from './AddEditFundingTip';
import { AssignmentStrategyComponent } from './AssignmentStrategy';
import { DeactivateVDriveModal } from './DeactivateVDriveModal';
import { FundingTipAmountAndAddMultiple } from './FundingTipAmountAndAddMultiple';
import { FundingTipComponent } from './FundingTipComponents';
import { OverdraftLimitField } from './OverdraftLimitField';
import { PaymentModeField } from './PaymentModeField';
import {
  AutoRechargeComponent,
  AutoRechargeWhenComponent,
} from './onboarding/SetAutoRecharge';
import { PickupInstructions } from './PickupInstructions';
import { ProvidersVDrive } from './ProvidersVDrive';
import { VDriveCards } from './VDriveCards';
import { VDriveCreditCard } from './VDriveCreditCard';
import {
  PinnedFleet,
  VDriveFleetPicker,
  pinnedFleetOf,
} from './VDriveFleetPicker';
import { ACH_CUSTOMER_ID_TOOLTIP, areProvidersEqual } from './vDriveHelpers';

export const VDriveLocation = () => {
  const {
    locationVDrive,
    deliveryAssignment,
    setDeliveryAssignment,
    applyDeliveryAssignmentToAllBrands,
    setLocationVDrive,
  } = useVDrive();
  const [loading, setLoading] = useState(false);

  const [merchantId, setMerchantId] = useState('');
  const [achCustomerId, setAchCustomerId] = useState('');
  const [enableVgrubsBrands, setEnableVgrubsBrands] = useState(false);
  const [autoAssignDriver, setAutoAssignDriver] = useState(false);
  const [providers, setProviders] = useState<VDriveProviderInfo[]>([]);
  const navigate = useNavigate();
  const [autoRechargeAmount, setAutoRechargeAmount] = useState<number | null>(
    0
  );
  const [autoRechargeWhenAmount, setAutoRechargeWhenAmount] = useState<
    number | null
  >(null);
  const [balance, setBalance] = useState(0);
  const [rechargeCollapse, setRechargeCollapse] = useState(false);
  const [vGrubsFee, setVGrubsFee] = useState(25);
  const [vDriveFee, setVDriveFee] = useState(200);
  const [cwTeamId, setCwTeamId] = useState('');
  const [fleet, setFleet] = useState<PinnedFleet | null>(null);
  const [fleetPickerAvailable, setFleetPickerAvailable] = useState(true);
  // The pinned fleet's code when vDrive says it no longer takes our orders.
  const [unlinkedFleetId, setUnlinkedFleetId] = useState<string | null>(null);
  const [pickupInstructions, setPickupInstructions] = useState('');
  const [fundedTipSchedule, setFundedTipSchedule] = useState<ScheduleEntity[]>(
    []
  );
  const [fundedTipAddEditModal, setFundedTipAddEditModal] =
    useState<ScheduleEntity | null>(null);
  const [fundedTipAdd, setFundedTipAdd] = useState(false);

  const [fundingMultiple, setFundingMultiple] = useState<
    ScheduleEntity[] | null
  >(null);

  const [deactivateModal, setDeactivateModal] = useState(false);
  const [isVDriveDeactivated, setIsVDriveDeactivated] = useState(false);
  const [isVDriveInProgress, setIsVDriveInProgress] = useState(false);
  const [isProvidersDisabled, setIsProvidersDisabled] = useState(false);
  const [exclusiveOfferEnabled, setExclusiveOfferEnabled] = useState(false);
  const [callDispatcherEnabled, setCallDispatcherEnabled] = useState(false);
  const [motoClickEnabled, setMotoClickEnabled] = useState(false);
  const [interceptAllDeliveries, setInterceptAllDeliveries] = useState(true);
  const callDispatcherMinutesRef = useRef<HTMLInputElement>(null);
  const [callDispatcherMinutes, setCallDispatcherMinutes] = useState<
    number | null
  >(null);
  const autoRecharge = useMeasuredHeight<HTMLDivElement>();
  const interceptor = useMeasuredHeight<HTMLDivElement>();

  const { id } = useParams() as { id: string };

  async function handleUpdateVDrive() {
    if (!merchantId) {
      ErrorToast('Please add Delivery Merchant ID');
      return;
    }
    if (Number(vDriveFee) < 80) {
      ErrorToast(`vDrive Fee can't be less than $0.80`);
      return;
    }
    if (Number(vGrubsFee) < 0) {
      ErrorToast(`vGrubs Fee can't be less than $0.00`);
      return;
    }

    if (
      callDispatcherEnabled &&
      !interceptAllDeliveries &&
      !callDispatcherMinutes
    ) {
      ErrorToast('Please add call dispatcher minutes');
      return;
    }
    const providersDisabled =
      areProvidersEqual(providers, locationVDrive?.providers) ||
      isProvidersDisabled
        ? null
        : providers
            .filter(({ enabled }) => enabled === false)
            .map(({ provider }) => provider);

    const sanitizedDeliveryAssignment = deliveryAssignment
      ? sanitizeDeliveryAssignment(
          deliveryAssignment as DeliveryAssignmentEntity
        )
      : null;

    const trimmedPickupInstructions = pickupInstructions.trim();

    const body: ManageLocationVDriveRequestData = {
      merchantId,
      pickupInstructions: trimmedPickupInstructions || null,
      enableVgrubsBrands,
      exclusiveOfferEnabled,
      callDispatcherEnabled,
      autoAssignDriver,
      providersDisabled,
      autoRechargeAmount,
      autoRechargeWhenAmount,
      achCustomerId,
      vDriveFee,
      vGrubsFee,
      fundedTipSchedule: groupFundingTipSchedule(fundedTipSchedule),
      deliveryAssignment: sanitizedDeliveryAssignment,
      applyDeliveryAssignenmentToAllBrands: applyDeliveryAssignmentToAllBrands,
      cwTeamId: cwTeamId.length ? cwTeamId : null,
      // Left out when the environment has no vDrive key: omitted keeps the pin.
      ...(fleetPickerAvailable && { vdriveOrganizationId: fleet?.id ?? null }),
      motoClickEnabled,
      callDispatcherMinutes:
        !callDispatcherEnabled || interceptAllDeliveries
          ? null
          : callDispatcherMinutes,
    };

    try {
      setLoading(true);
      const { data } = await api.put(`/v1/vdrive/location/${id}`, body);
      SuccessToast(data.message);
      navigate(-1);
      if (locationVDrive) {
        setLocationVDrive({
          ...locationVDrive,
          merchantId,
          cwTeamId,
          ...(fleetPickerAvailable && {
            vdriveOrganizationId: fleet?.id ?? null,
            vdriveOrganizationName: fleet?.name ?? null,
          }),
          pickupInstructions: trimmedPickupInstructions || null,
          vGrubsBrandsEnabled: enableVgrubsBrands,
          exclusiveOfferEnabled,
          callDispatcherEnabled,
          autoAssign: autoAssignDriver,
          providers: providers,
          autoRechargeAmount: autoRechargeAmount ?? 0,
          autoRechargeWhenAmount: autoRechargeWhenAmount ?? 0,
          achCustomerId,
          vDriveFee,
          vGrubsFee,
          fundedTipSchedule: groupFundingTipSchedule(fundedTipSchedule),
          deliveryAssignment: deliveryAssignment as DeliveryAssignmentEntity,
          motoClickEnabled,
          callDispatcherMinutes:
            !callDispatcherEnabled || interceptAllDeliveries
              ? null
              : callDispatcherMinutes,
        });
      }
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleGetVDrive() {
    try {
      setLoading(true);
      if (locationVDrive?.location?.id !== Number(id)) {
        const { data } = await getLocationVDriveById(id);

        if (data?.data?.status === VDriveStatus.rejected) {
          return navigate(`/locations/v-drive/onboarding/${id}?step=0`, {
            replace: true,
            state: data,
          });
        }

        if (data?.data?.status === VDriveStatus.inProgress) {
          setIsVDriveInProgress(true);
        }
        if (data?.data?.status !== VDriveStatus.active) {
          setIsProvidersDisabled(true);
        }
        setLocationVDrive({
          ...data.data,
          providers:
            data?.data?.providers.filter(
              (provider: VDriveProviderInfo) =>
                String(provider.provider) !== 'unknown'
            ) || [],
        });
        setAutoAssignDriver(data?.data?.autoAssign || false);
        setEnableVgrubsBrands(data?.data?.vGrubsBrandsEnabled || false);
        setExclusiveOfferEnabled(data?.data?.exclusiveOfferEnabled || false);
        setCallDispatcherEnabled(data?.data?.callDispatcherEnabled || false);
        setMerchantId(data?.data?.merchantId || '');
        setFleet(pinnedFleetOf(data?.data));
        setPickupInstructions(data?.data?.pickupInstructions || '');
        setProviders(
          data?.data?.providers.filter(
            (provider: VDriveProviderInfo) =>
              String(provider.provider) !== 'unknown'
          ) || []
        );
        setAutoRechargeAmount(data?.data?.autoRechargeAmount || null);
        setAutoRechargeWhenAmount(data?.data?.autoRechargeWhenAmount || null);
        setBalance(
          (data?.data?.totalBalance || 0) - (data?.data?.reservedBalance || 0)
        );
        setAchCustomerId(data?.data?.achCustomerId || '');
        setVGrubsFee(data?.data?.vGrubsFee || 0);
        setVDriveFee(data?.data?.vDriveFee || 0);
        setFundedTipSchedule(
          data?.data?.fundedTipSchedule
            ? unGroupFundingTipSchedule(data?.data?.fundedTipSchedule)
            : []
        );
        if (data?.data?.status === VDriveStatus.disabled) {
          setIsVDriveDeactivated(true);
        }
        setDeliveryAssignment(data?.data?.deliveryAssignment || null);
        setCwTeamId(data?.data?.cwTeamId || '');
        setMotoClickEnabled(data?.data?.motoClickEnabled || false);
      } else {
        setAutoAssignDriver(locationVDrive?.autoAssign || false);
        setEnableVgrubsBrands(locationVDrive?.vGrubsBrandsEnabled || false);
        setExclusiveOfferEnabled(
          locationVDrive?.exclusiveOfferEnabled || false
        );
        setCallDispatcherEnabled(
          locationVDrive?.callDispatcherEnabled || false
        );
        setCallDispatcherMinutes(locationVDrive?.callDispatcherMinutes || null);
        setInterceptAllDeliveries(
          locationVDrive?.callDispatcherMinutes ? false : true
        );
        setMerchantId(locationVDrive?.merchantId || '');
        setFleet(pinnedFleetOf(locationVDrive));
        setPickupInstructions(locationVDrive?.pickupInstructions || '');
        setProviders(
          locationVDrive?.providers.filter(
            (provider: VDriveProviderInfo) =>
              String(provider.provider) !== 'unknown'
          ) || []
        );
        setAutoRechargeAmount(locationVDrive?.autoRechargeAmount || null);
        setAutoRechargeWhenAmount(
          locationVDrive?.autoRechargeWhenAmount || null
        );
        setBalance(
          (locationVDrive?.totalBalance || 0) -
            (locationVDrive?.reservedBalance || 0)
        );
        setAchCustomerId(locationVDrive?.achCustomerId || '');
        setVGrubsFee(locationVDrive?.vGrubsFee || 0);
        setVDriveFee(locationVDrive?.vDriveFee || 0);
        setFundedTipSchedule(
          locationVDrive?.fundedTipSchedule
            ? unGroupFundingTipSchedule(locationVDrive?.fundedTipSchedule)
            : []
        );
        setCwTeamId(locationVDrive?.cwTeamId || '');
        if (locationVDrive?.status === VDriveStatus.disabled) {
          setIsVDriveDeactivated(true);
        }
        if (locationVDrive?.status === VDriveStatus.inProgress) {
          setIsVDriveInProgress(true);
        }
        if (locationVDrive?.status !== VDriveStatus.active) {
          setIsProvidersDisabled(true);
        }
        if (!deliveryAssignment) {
          setDeliveryAssignment(locationVDrive?.deliveryAssignment || null);
        }
        setMotoClickEnabled(locationVDrive?.motoClickEnabled || false);
        setCallDispatcherEnabled(
          locationVDrive?.callDispatcherEnabled || false
        );
        setCallDispatcherMinutes(locationVDrive?.callDispatcherMinutes || null);
        setInterceptAllDeliveries(
          locationVDrive?.callDispatcherMinutes ? false : true
        );
      }
    } catch (error) {
      if (isAxiosError(error)) {
        if (error?.response?.status === 404) {
          navigate(`/locations/v-drive/onboarding/${id}?step=0`, {
            replace: true,
            state: locationVDrive,
          });
        } else {
          reportError(error);
        }
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (id) {
      handleGetVDrive();
    }
  }, [id, locationVDrive?.location?.id]);

  const pinnedFleetId = locationVDrive?.vdriveOrganizationId;

  // Asks vDrive, so it runs beside the page instead of holding it up. Only a
  // clear "not linked" is shown; a failure says nothing either way.
  useEffect(() => {
    setUnlinkedFleetId(null);
    if (!id || !pinnedFleetId) return;
    let cancelled = false;
    getLocationFleetOrganization(id)
      .then(({ data }) => {
        if (!cancelled && data.data.linked === false) {
          setUnlinkedFleetId(data.data.organizationId);
        }
      })
      .catch((error) => reportError(error, { silent: true }));
    return () => {
      cancelled = true;
    };
  }, [id, pinnedFleetId]);

  async function handleDeactivateReactivate() {
    try {
      setLoading(true);
      const { data } = await api.post(
        `/v1/vdrive/${locationVDrive?.id}/${
          isVDriveDeactivated ? 'reactivate' : 'disable'
        }`
      );
      setIsVDriveDeactivated((prev) => !prev);
      setDeactivateModal(false);

      SuccessToast(data.message);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const { location, owner, cardFailed } = locationVDrive || {};
  // Read from the store rather than mirrored into state: Edit Card writes
  // the store, and a location that just got its own card is off the vGrubs
  // vDrive card at once.
  const isVDriveCard = locationVDrive?.isVDriveCard ?? false;

  const SaveButton = () => (
    <Button $selected onClick={handleUpdateVDrive}>
      Save
    </Button>
  );

  const DeactivateButton = () => (
    <Button
      $gray={!isVDriveDeactivated}
      $selected={isVDriveDeactivated}
      onClick={() => setDeactivateModal(true)}
    >
      {isVDriveDeactivated ? 'Reactivate' : 'Deactivate'} vDrive
    </Button>
  );

  const ActivateButton = () => (
    <Button
      $selected
      $darkBlue
      onClick={() => {
        navigate(
          `/locations/v-drive/onboarding/${locationVDrive?.location?.id}?step=0`
        );
      }}
    >
      Activate vDrive
    </Button>
  );

  const handleValueVDriveFee = useCallback((val) => {
    setVDriveFee(val);
  }, []);

  const handleValueVGrubsFee = useCallback((val) => {
    setVGrubsFee(val);
  }, []);

  const disabledOrInactive =
    locationVDrive?.status === VDriveStatus.inactive ||
    locationVDrive?.status === VDriveStatus.disabled;

  const perDelivery =
    locationVDrive?.paymentMode === VDrivePaymentMode.perDelivery;

  return (
    <>
      <AdminHeader title={'Manage vDrive Location'}></AdminHeader>

      {loading && <AdminSpinner />}
      {locationVDrive && (
        <>
          <Div
            $m="40px 0"
            $mSM="20px 0"
            $flex
            $alignCenter
            $between
            $columnSM
            $gap="48px"
            $gapSM="20px"
          >
            <Div
              $flex
              $alignCenter
              $gap="30px"
              $gapSM="16px"
              $width="70%"
              $widthSM="100%"
            >
              <VDriveLogo width="125" height="125" />
              <Div>
                <H3 $mb="0" $color={lightTheme.primary}>
                  {location?.name}
                </H3>
                <P $mt="8">
                  {location?.address}
                  <br />
                  Owner: {owner?.firstName} {owner?.lastName} <br />
                  Phone Number: {location?.phoneNumber || '/'}
                </P>
                {isVDriveInProgress && (
                  <P $mt="8" $color={lightTheme.primary}>
                    vDrive verification is in progress.
                  </P>
                )}
              </Div>
            </Div>
            <Div
              $flex
              $column
              $alignEnd
              $desktop
              $width="30%"
              $widthSM="100%"
              $gap="16px"
            >
              <SaveButton />
              {!disabledOrInactive && <DeactivateButton />}
              {disabledOrInactive && <ActivateButton />}
            </Div>
          </Div>
          <Div $flex $gap="48px" $gapSM="24px" $columnSM $m="20px 0">
            <Div $width="500px" $widthSM="100%" $flex $column $gap="24px">
              {!disabledOrInactive && (
                <VDriveCreditCard
                  balance={balance}
                  setBalance={setBalance}
                  location={location}
                  isVDriveCard={isVDriveCard}
                  setLoading={setLoading}
                  vDriveVerification={locationVDrive}
                  handleGetVDrive={handleGetVDrive}
                  cardFailed={cardFailed ?? false}
                />
              )}

              {!disabledOrInactive && (
                <VDriveCards
                  vDrive={locationVDrive}
                  onChanged={handleGetVDrive}
                  setLoading={setLoading}
                />
              )}

              {!disabledOrInactive && (
                <PaymentModeField
                  vDrive={locationVDrive}
                  onSaved={(amounts) => {
                    setAutoRechargeAmount(amounts.autoRechargeAmount);
                    setAutoRechargeWhenAmount(amounts.autoRechargeWhenAmount);
                  }}
                />
              )}

              {!disabledOrInactive && (
                <Div $flex $column $gap="8px">
                  <OverdraftLimitField vDrive={locationVDrive} />
                  <Disclaimer
                    $m="0"
                    $center
                    $pointer
                    $underline
                    $color={lightTheme.primary}
                    onClick={() =>
                      navigate(
                        generatePath(ADMIN_RECEIVABLES_LOCATION, {
                          locationId: id,
                        })
                      )
                    }
                  >
                    See what this location owes and its payment history
                  </Disclaimer>
                </Div>
              )}

              <AdminInput
                tooltip="First Delivery Merchant ID used to connect vDrive."
                label="Delivery Merchant ID"
                placeholder="Enter Here"
                $textCenter
                value={merchantId}
                onChange={(e) => setMerchantId(e.target.value)}
              />

              <AdminInput
                tooltip="Cartwheel Team ID used to connect vDrive."
                label="Team ID"
                placeholder="Enter Here"
                $textCenter
                value={cwTeamId}
                onChange={(e) => setCwTeamId(e.target.value)}
              />

              <VDriveFleetPicker
                value={fleet}
                onChange={setFleet}
                unlinked={!!fleet && fleet.id === unlinkedFleetId}
                helperText="Brands synced with this location get the same fleet."
                onUnavailable={() => setFleetPickerAvailable(false)}
              />

              {!disabledOrInactive && (
                <AdminInput
                  tooltip={ACH_CUSTOMER_ID_TOOLTIP}
                  $textCenter
                  label="ACH Customer ID"
                  placeholder="Enter here"
                  value={achCustomerId}
                  onChange={(e) => setAchCustomerId(e.target.value)}
                />
              )}

              <Div $flex $gap="24px" $gapSM="12px">
                <Div $width="50%">
                  <AdminDollarInput2
                    label="vDrive Fee"
                    onValueChange={handleValueVDriveFee}
                    value={vDriveFee}
                    $textCenter
                  />
                </Div>

                <Div $width="50%">
                  <AdminDollarInput2
                    label="vGrubs Fee"
                    onValueChange={handleValueVGrubsFee}
                    value={vGrubsFee}
                    $textCenter
                  />
                </Div>
              </Div>

              <PickupInstructions
                value={pickupInstructions}
                onChange={setPickupInstructions}
                helperText="Applies to this location only - virtual brands have their own. Leave it empty to send nothing."
              />
            </Div>
            <Div $width="calc(100% - 500px)" $widthSM="100%">
              <ProvidersVDrive
                providers={providers}
                setProviders={setProviders}
                disabled={isProvidersDisabled}
              />

              <Div $mt="16" $mtSM="8"></Div>

              {/* Per delivery the amounts are only kept for a switch to
                  top-up, which the Payment field asks for again. */}
              {!disabledOrInactive && !perDelivery && (
                <Div $box $flex $column $p="24px">
                  <Div
                    $pointer
                    $flex
                    $alignCenter
                    $between
                    onClick={() => setRechargeCollapse((prev) => !prev)}
                  >
                    <H5 $mb="0">Set Auto Recharge</H5>
                    <Icon $rotate={rechargeCollapse ? '180deg' : '0'}>
                      <ChevronSmall />
                    </Icon>
                  </Div>
                  <Collapsible
                    expanded={rechargeCollapse}
                    elementHeight={autoRecharge.height}
                  >
                    <Div ref={autoRecharge.ref} $flex $column $gap="4px">
                      <AutoRechargeComponent
                        autoRechargeAmount={autoRechargeAmount}
                        setAutoRechargeAmount={setAutoRechargeAmount}
                      />
                      <AutoRechargeWhenComponent
                        autoRechargeWhenAmount={autoRechargeWhenAmount ?? 0}
                        setAutoRechargeWhenAmount={setAutoRechargeWhenAmount}
                      />
                    </Div>
                  </Collapsible>
                </Div>
              )}
              <Div $mt="16" $mtSM="8"></Div>
              <FundingTipComponent
                setFundedTipSchedule={setFundedTipSchedule}
                fundedTipSchedule={fundedTipSchedule}
                setFundedTipAddEditModal={setFundedTipAddEditModal}
                setFundedTipAdd={setFundedTipAdd}
                setFundingMultiple={setFundingMultiple}
              />

              <Div $mt="16" $mtSM="8"></Div>

              <Div
                $width="100%"
                $box
                $p=" 0 24px"
                $height="80px"
                $heightSM="80px"
                $flex
                $alignCenter
                $between
              >
                <H5 $m="0">Enable vDrive for vGrubs Brands</H5>{' '}
                <AdminSwitch
                  checked={enableVgrubsBrands}
                  onChange={() => setEnableVgrubsBrands((prev) => !prev)}
                />
              </Div>
              <Div $mt="16" $mtSM="8"></Div>

              <Div
                $width="100%"
                $box
                $p=" 0 24px"
                $height="80px"
                $heightSM="80px"
                $flex
                $alignCenter
                $between
              >
                <SubTitleWithTooltipIcon
                  mb="0"
                  text={'Use Direct Motoclick'}
                  tooltip={
                    'This will enable direct integration with motoclick and use their drivers instead of first delivery motoclick.'
                  }
                />
                <AdminSwitch
                  checked={motoClickEnabled}
                  onChange={() => setMotoClickEnabled((prev) => !prev)}
                />
              </Div>
              <Div $mt="16" $mtSM="8"></Div>

              <Div
                $width="100%"
                $box
                $p=" 0 24px"
                $height="80px"
                $heightSM="80px"
                $flex
                $alignCenter
                $between
              >
                <SubTitleWithTooltipIcon
                  mb="0"
                  text={'Enable Tracking Link Offers'}
                  tooltip={
                    'Activate this to show a tracking link card where users can enter their phone number and receive exclusive future offers.'
                  }
                />
                <AdminSwitch
                  checked={exclusiveOfferEnabled}
                  onChange={() => setExclusiveOfferEnabled((prev) => !prev)}
                />
              </Div>
              <Div $mt="16" $mtSM="8"></Div>

              <Div $width="100%" $box $p="0 24px" $flex $justifyCenter $column>
                <Div
                  $flex
                  $alignCenter
                  $between
                  $height="80px"
                  $heightSM="80px"
                >
                  <SubTitleWithTooltipIcon
                    mb="0"
                    text={'Enable Interceptor Call'}
                    tooltip={
                      'When enabled, an AI interceptor agent will automatically call the customer after the delivery is created.'
                    }
                  />
                  <AdminSwitch
                    checked={callDispatcherEnabled}
                    onChange={() => setCallDispatcherEnabled((prev) => !prev)}
                  />
                </Div>
                <CollapsibleComponent
                  expanded={callDispatcherEnabled}
                  elementHeight={interceptor.height}
                >
                  <Div
                    ref={interceptor.ref}
                    $p="24px 0"
                    $flex
                    $column
                    $gap="8px"
                    $borderTop={callDispatcherEnabled}
                    $borderColor={lightTheme.gray}
                  >
                    <Div
                      $flex
                      $alignCenter
                      $gap="12px"
                      $pointer
                      onClick={() => {
                        setInterceptAllDeliveries(true);
                      }}
                      $opacity={!interceptAllDeliveries ? '0.5' : '1'}
                    >
                      <Radio checked={interceptAllDeliveries} />
                      <P $m="0">Intercept all deliveries</P>
                    </Div>
                    <Div
                      $flex
                      $alignCenter
                      $between
                      $gap="12px"
                      $opacity={interceptAllDeliveries ? '0.5' : '1'}
                    >
                      <Div
                        $flex
                        $alignCenter
                        $gap="12px"
                        $pointer
                        onClick={() => {
                          setInterceptAllDeliveries(false);
                          callDispatcherMinutesRef.current?.focus();
                        }}
                      >
                        <Radio checked={!interceptAllDeliveries} />
                        <P $m="0">
                          Intercept pending after order creation and no drivers
                          assigned after:
                        </P>
                      </Div>
                      <Div $width="100px" $widthSM="100px" $flexNone>
                        <AdminInput
                          placeholder="0"
                          value={
                            interceptAllDeliveries
                              ? ''
                              : callDispatcherMinutes ?? ''
                          }
                          onChange={(e) => {
                            const inputValue = e.target.value.replace(
                              /[^0-9]/g,
                              ''
                            );
                            setCallDispatcherMinutes(Number(inputValue));
                          }}
                          ref={callDispatcherMinutesRef}
                          onFocus={() => setInterceptAllDeliveries(false)}
                          $textCenter
                          suffix="min"
                        />
                      </Div>
                    </Div>
                  </Div>
                </CollapsibleComponent>
              </Div>
              <Div $mt="16" $mtSM="8"></Div>

              <Div
                $width="100%"
                $box
                $p=" 0 24px"
                $height="80px"
                $heightSM="80px"
                $flex
                $alignCenter
                $between
              >
                <Div>
                  <H5 $mb="4">Automatically Assign Driver</H5>
                  <Disclaimer $m="0" $color={lightTheme.lightGray}>
                    (Based on cheapest and fastest routing)
                  </Disclaimer>
                </Div>
                <AdminSwitch
                  checked={autoAssignDriver}
                  onChange={() => setAutoAssignDriver((prev) => !prev)}
                />
              </Div>
              <Div $mt="16" $mtSM="8"></Div>
              <AssignmentStrategyComponent
                isCustomized={locationVDrive?.deliveryAssignment !== null}
                id={id}
              />
            </Div>
          </Div>

          <Div $mobile $mt="40" $flex $column $gap="16px">
            <SaveButton />
            <DeactivateButton />
          </Div>
        </>
      )}

      {isVDriveDeactivated && (
        <AnimatedDiv $flex $column $center $minHeight="80vh">
          <Img alt="" $width="180px" src={vDriveDeactivated} $mb="24" />
          <H3 $mb="24" $center>
            vDrive Deactivated!
          </H3>
          <P $mb="32" $center>
            vDrive service for this location is deactivated. <br />
            Click on button and activate it again.
          </P>
          <DeactivateButton />
        </AnimatedDiv>
      )}

      <DeactivateVDriveModal
        modalIsOpen={deactivateModal}
        closeModal={() => setDeactivateModal(false)}
        handleDeactivateReactivate={handleDeactivateReactivate}
        isVDriveDeactivated={isVDriveDeactivated}
      />

      <AddEditFundingTip
        modalIsOpen={fundedTipAddEditModal}
        closeModal={() => setFundedTipAddEditModal(null)}
        setFundedTipSchedule={setFundedTipSchedule}
        edit
      />

      <AddEditFundingTip
        modalIsOpen={fundedTipAdd}
        closeModal={() => setFundedTipAdd(false)}
        setFundedTipSchedule={setFundedTipSchedule}
      />

      <FundingTipAmountAndAddMultiple
        modalIsOpen={fundingMultiple}
        closeModal={() => setFundingMultiple(null)}
        setFundedTipSchedule={setFundedTipSchedule}
      />
    </>
  );
};

export const Collapsible = styled.div<{
  expanded: boolean;
  elementHeight?: number;
}>`
  opacity: ${(props) => (props.expanded ? '1' : '0')};
  visibility: ${(props) => (props.expanded ? 'visible' : 'hidden')};
  max-height: ${({ expanded, elementHeight }) =>
    expanded ? `${elementHeight}px` : '0px'};
  margin-top: ${(props) => (props.expanded ? '24px' : '0px')};
  /* Collapsed, the box is 0px tall but its content still hangs out below it,
     invisible, and stretches the scroll area into a blank gap. So it clips
     while closed and while opening, and lets go once open (300ms), where the
     shadows and dropdowns inside must not be cut off. Without
     allow-discrete support it lets go at once, which only shows mid-open. */
  overflow: ${(props) => (props.expanded ? 'visible' : 'hidden')};
  transition: all 300ms,
    overflow 0s ${(props) => (props.expanded ? '300ms' : '0s')};
  transition-behavior: allow-discrete;

  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;
